// @ts-strict-ignore
import {produce} from 'immer';
import sum from 'lodash/sum';
import partition from 'lodash/partition';
import isNumber from 'lodash/isNumber';
import React, {useMemo, useRef, useState} from 'react';

import {useTranslation} from 'common/i18n/hooks';
import {InputSize, InputType, ValidationStatus} from 'common/components/input/types';
import {InputReact} from 'common/components/input';
import {useStagedState} from 'common/hooks/useStagedState';
import {parseNumber} from 'common/util';
import {reporter} from 'common/report/error-reporting';
import {DisplaySize} from 'common/components/tooltip/types';
import {ButtonTheme} from 'common/components/button/constants';
import {SelectTheme} from 'common/components/select/constants';
import {withStyles} from 'common/util/react';
import {ButtonReact} from 'common/components/button';
import {InputLabelReact} from 'common/components/input/label';
import {SelectReact} from 'common/components/select';

import {
  CustomMultiAttribution,
  FreePlanAllowedMultiAttributionTypes,
  MultiAttribution,
  MultiAttributionSectionItem,
  MultiAttributionType,
  MultiAttributionTypeList,
  MultiAttributionWeights,
  RemoveAttributionValue,
} from './types';
import {AttributionTypeToSpec, CustomDefaultWeights} from './constants';
import styles from './index.module.styl';
import css from './index.module.styl?inline';

interface Props {
  multiAttribution: MultiAttribution;
  metricIndex: number;
  onCancel?: () => void;
  onChange?: (attribution: MultiAttribution) => void;
  onDelete?: () => void;
  isSupportedMultiAttributionType: (attributionType: MultiAttributionType) => boolean;
  showMultiAttributionType?: (attributionType: MultiAttributionType) => boolean;
  projectIsFreePlan: boolean;
}

function isAttributionTypeEnabledForProject(
  attributionType: MultiAttributionType,
  projectIsFreePlan: boolean,
): boolean {
  return !projectIsFreePlan || FreePlanAllowedMultiAttributionTypes.includes(attributionType);
}

function WeightInputs(props: {
  disabled: boolean;
  weights: MultiAttributionWeights | null;
  onWeightIndexFocused?: (index: number) => void;
  onWeightChange?: (index: number, value: string) => void;
  invalidWeightIndex?: number;
  invalidWeightsText?: string;
}) {
  const {t} = useTranslation();

  if (!props.weights) {
    return null;
  }

  const {first, middle, last} = props.weights;
  const inputSpecs = [
    {weight: first, label: t(`query-builder.attribution.weight-first`)},
    {weight: middle, label: t(`query-builder.attribution.weight-middle`)},
    {weight: last, label: t(`query-builder.attribution.weight-last`)},
  ];

  return (
    <>
      <div className={styles.weightsSection} elref='multiAttributionWeightsSection'>
        {inputSpecs.map((inputSpec, weightIndex) => {
          return (
            <InputReact
              key={weightIndex}
              disabled={props.disabled}
              label={inputSpec.label}
              size={InputSize.Medium}
              type={InputType.Number}
              min='0'
              maxlength={3}
              elref='multiAttributionWeightsInput'
              validationStatus={
                props.invalidWeightIndex === weightIndex ? ValidationStatus.Invalid : ValidationStatus.Default
              }
              value={inputSpec.weight?.toString()}
              onInput={(ev) => props.onWeightChange?.(weightIndex, ev.target.value)}
              onFocus={() => props.onWeightIndexFocused?.(weightIndex)}
            />
          );
        })}
      </div>
      {props.invalidWeightsText && (
        <div elref='attributionValidationError' className={styles.validationError}>
          {props.invalidWeightsText}
        </div>
      )}
    </>
  );
}

export const MultiAttributionMenu = withStyles((props: Props) => {
  const rootEl = useRef<HTMLDivElement>(null);

  const [stagedMultiAttribution, setStagedMultiAttribution] = useStagedState(props.multiAttribution, props.onChange);
  const [focusedAttributionType, setFocusedAttributionType] = useState<MultiAttributionType | null>(
    stagedMultiAttribution.type,
  );
  const [invalidWeightsText, setInvalidWeightsText] = useState(``);
  const [lastFocusedWeightIndex, setLastFocusedWeightIndex] = useState(-1);

  const {t} = useTranslation();

  const defaultCustomName = t(`query-builder.attribution.custom-model-num`, {number: props.metricIndex + 1});

  // if multi attribution changes from outside, update the focused type if it's not already set
  if (props.multiAttribution !== stagedMultiAttribution && !focusedAttributionType) {
    setFocusedAttributionType(props.multiAttribution.type);
  }

  const sectionsWithItems = useMemo(() => {
    const multiAttributionItems: MultiAttributionSectionItem[] = MultiAttributionTypeList.filter((attributionType) =>
      props.showMultiAttributionType ? props.showMultiAttributionType(attributionType) : true,
    ).map((attributionType) => {
      const isSupportedMultiAttributionType = props.isSupportedMultiAttributionType(attributionType);
      // Check for enterprise only perms
      const isEnabledForProject = isAttributionTypeEnabledForProject(attributionType, props.projectIsFreePlan);

      return {
        icon: AttributionTypeToSpec[attributionType].icon,
        label: AttributionTypeToSpec[attributionType].label,
        value: attributionType,
        isSelected: attributionType === stagedMultiAttribution?.type,
        isDisabled: !isSupportedMultiAttributionType || !isEnabledForProject,
        tooltip:
          !isSupportedMultiAttributionType && isEnabledForProject
            ? {
                text: t(`query-builder.attribution.error.count-type`),
                size: DisplaySize.Small,
              }
            : null,
      };
    });

    const [predefinedItems, customItems] = partition(
      multiAttributionItems,
      (item) => item.value !== MultiAttributionType.Custom,
    );

    const removeAttributionItem: MultiAttributionSectionItem = {
      description: null,
      icon: `x`,
      isSelected: false,
      label: t(`query-builder.attribution.remove`),
      value: RemoveAttributionValue,
      // TODO: need this noop to prevent positioning issue with context panel DropMenu when initially opening the
      // the select menu.
      key: Date.now(),
    };
    return [
      {
        items: [...predefinedItems],
      },
      {
        items: [...customItems, removeAttributionItem],
      },
    ];
  }, [
    stagedMultiAttribution,
    props.projectIsFreePlan,
    props.isSupportedMultiAttributionType,
    props.showMultiAttributionType,
  ]);

  const stagedMultiAttributionWeights =
    stagedMultiAttribution.type === MultiAttributionType.Custom
      ? stagedMultiAttribution.weights
      : AttributionTypeToSpec[stagedMultiAttribution.type].weights;
  const focusedAttributionIsDisabled = sectionsWithItems[0].items.find(
    (items) => items.value === focusedAttributionType,
  )?.isDisabled;
  const multiAttributionIsCustom = stagedMultiAttribution.type === MultiAttributionType.Custom;

  // When there are invalid weights for Custom, we highlight the last focused input
  const invalidWeightIndex = invalidWeightsText && lastFocusedWeightIndex;

  const updateFocusedAttributionType = (item: MultiAttributionSectionItem) => {
    // Handle the Remove Attribution button case
    setFocusedAttributionType(item.value === RemoveAttributionValue ? null : item.value);
  };

  const onWeightChange = (weightIndex: number, value: string) => {
    const newWeight = parseNumber(value);

    const oldWeights = stagedMultiAttributionWeights;
    const newWeights: number[] = [oldWeights.first, oldWeights.middle, oldWeights.last];
    newWeights[weightIndex] = newWeight;

    let invalidWeightsText = null;
    // Every individual value must be valid
    if (newWeights.some((weight) => !isNumber(weight) || weight < 0 || weight > 100)) {
      invalidWeightsText = t(`query-builder.attribution.error.weight-value`);
    }
    // And the total must add up to exactly 100
    else if (sum(newWeights) !== 100) {
      invalidWeightsText = t(`query-builder.attribution.error.weight-sum`);
    }

    setInvalidWeightsText(invalidWeightsText);
    setLastFocusedWeightIndex(weightIndex);
    setStagedMultiAttribution(
      produce((draft) => {
        const partialUpdate: Partial<CustomMultiAttribution> = {
          weights: {
            first: newWeights[0],
            middle: newWeights[1],
            last: newWeights[2],
          },
        };
        if (draft.type !== MultiAttributionType.Custom) {
          partialUpdate.type = MultiAttributionType.Custom;
          partialUpdate.name = defaultCustomName;
        }
        Object.assign(draft, partialUpdate);
      }, stagedMultiAttribution),
    );
  };

  const onAttributionTypeChange = (newAttributionType: MultiAttributionType) => {
    const shouldCommit = newAttributionType !== MultiAttributionType.Custom;

    setStagedMultiAttribution(
      produce((draft) => {
        const oldAttributionType = draft.type;
        draft.type = newAttributionType;

        if (draft.type === MultiAttributionType.Custom) {
          const oldWeights = AttributionTypeToSpec[oldAttributionType].weights;
          draft.weights = oldWeights
            ? {...oldWeights}
            : // Default weights for custom model if change from a model without first/middle/last inputs
              CustomDefaultWeights;
          draft.name = defaultCustomName;
        } else {
          draft.weights = AttributionTypeToSpec[draft.type].weights;
        }
      }, stagedMultiAttribution),
      shouldCommit,
    );
  };

  const onCustomNameChange = (name: string) => {
    setStagedMultiAttribution(
      produce((draft) => {
        if (draft.type === MultiAttributionType.Custom) {
          draft.name = name || defaultCustomName;
        } else {
          reporter.error(`onCustomNameChange: unexpected MultiAttributionType`, {type: draft.type});
        }
      }, stagedMultiAttribution),
    );
  };

  return (
    <div elref='multiAttributionMenu' className={styles.content} ref={rootEl}>
      <InputLabelReact label={t(`query-builder.attribution.model-header`)} />
      <SelectReact
        className={styles.typeSelect}
        theme={SelectTheme.Secondary}
        menuHeight={450}
        disableSearch
        open={!multiAttributionIsCustom}
        sections={sectionsWithItems}
        contextPanelPositionParentElement={rootEl.current}
        onChange={(ev) => {
          const attributionType = ev.detail.item.value;
          if (attributionType === RemoveAttributionValue) {
            props.onDelete?.();
          } else {
            onAttributionTypeChange(attributionType);
          }
        }}
        onFocusItem={(event) => updateFocusedAttributionType(event.detail.item as MultiAttributionSectionItem)}
      >
        <div slot='context-panel-content'>
          <ContextPanelContent
            focusedAttributionIsDisabled={focusedAttributionIsDisabled}
            focusedAttributionType={focusedAttributionType}
            projectIsFreePlan={props.projectIsFreePlan}
            invalidWeightsText={invalidWeightsText}
            invalidWeightIndex={invalidWeightIndex}
          />
        </div>
      </SelectReact>
      {multiAttributionIsCustom && (
        <InputReact
          className={styles.customNameInput}
          label={t(`query-builder.attribution.name-header`)}
          size={InputSize.Medium}
          elref='multiAttributionNameInput'
          onChange={(ev) => onCustomNameChange(ev.detail.value)}
          value={stagedMultiAttribution.name}
        />
      )}
      {stagedMultiAttributionWeights && multiAttributionIsCustom && (
        <WeightInputs
          weights={stagedMultiAttributionWeights}
          disabled={false}
          invalidWeightIndex={invalidWeightIndex}
          invalidWeightsText={invalidWeightsText}
          onWeightChange={onWeightChange}
          onWeightIndexFocused={setLastFocusedWeightIndex}
        />
      )}
      <div className={styles.footerSection}>
        <ButtonReact theme={ButtonTheme.Tertiary} onClick={props.onCancel}>
          {t(`common.cancel`)}
        </ButtonReact>
        <ButtonReact
          elref='multiAttributionApplyButton'
          theme={ButtonTheme.Primary}
          disabled={!!invalidWeightsText}
          onClick={() => props.onChange?.(stagedMultiAttribution)}
        >
          {t(`common.apply`)}
        </ButtonReact>
      </div>
    </div>
  );
}, css);

function ContextPanelContent(props: {
  focusedAttributionType: MultiAttributionType;
  focusedAttributionIsDisabled: boolean;
  projectIsFreePlan: boolean;
  invalidWeightIndex: number;
  invalidWeightsText: string;
}) {
  const {t, print} = useTranslation();

  if (!props.focusedAttributionType) {
    return null;
  }

  const focusedAttribution = AttributionTypeToSpec[props.focusedAttributionType];
  const focusedAttributionTypeWeights = props.focusedAttributionType
    ? AttributionTypeToSpec[props.focusedAttributionType].weights
    : null;
  const focusedAttributionTypeIsCustom = props.focusedAttributionType === MultiAttributionType.Custom;

  if (!isAttributionTypeEnabledForProject(props.focusedAttributionType, props.projectIsFreePlan)) {
    return (
      <div className={styles.dropdownContent}>
        <div className={styles.dropdownTitle}>Advanced Attribution Models</div>
        <div className={styles.dropdownDescription}>{t(`query-builder.attribution.upgrade-cta`)}</div>
        <div className={styles.dropdownAction}>
          <ButtonReact
            theme={ButtonTheme.Primary}
            onClick={() => window.open(`https://docs.mixpanel.com/docs/analysis/advanced/attribution`, `_blank`)}
          >
            {t(`common.learn-more`)}
          </ButtonReact>
        </div>
      </div>
    );
  } else if (!props.focusedAttributionIsDisabled) {
    return (
      <div className={styles.dropdownContent}>
        <div className={styles.dropdownTitle}>{print(focusedAttribution.label)}</div>
        <div className={styles.dropdownDescription}>{print(focusedAttribution.description)}</div>
        {focusedAttributionTypeWeights && !focusedAttributionTypeIsCustom && (
          <WeightInputs
            weights={focusedAttributionTypeWeights}
            disabled
            invalidWeightIndex={props.invalidWeightIndex}
            invalidWeightsText={props.invalidWeightsText}
          />
        )}
      </div>
    );
  }

  return null;
}
